/**
 * Window appearance defaults — ONE copy, because a window is drawn TWICE.
 *
 * The 3D renderer (`3d/geometry/Window.jsx` + `WindowWithTrim.js`), the
 * elevation schematic (`elevations/schematics/`), and the properties panel all
 * have to answer "what does a window with no stored grille look like?" the
 * same way. They did not: 3D and the panel said `colonial`, both schematics
 * said `none`. Since nothing persisted `grillePattern` at all until the field
 * was added to `APPEARANCE_FIELDS_BY_CATEGORY`, EVERY window took a default —
 * so the same window drew muntins in 3D and bare glass on the elevation, for
 * the whole life of the code.
 *
 * `colonial` wins because it is what the app already SAYS: the panel opens on
 * "Colonial" for a window with no stored pattern, and the 3D view draws one.
 * The elevation was the surface disagreeing with the other three.
 */
export const DEFAULT_GRILLE_PATTERN = 'colonial';

/**
 * The divided-light grid a window falls back to when it has none stored.
 *
 * Same failure as `DEFAULT_GRILLE_PATTERN` above, one field over and missed
 * when that one was fixed: 3D (`WindowWithTrim`'s options default) and the
 * properties panel both said 2x2, while the elevation's `normalizePanes` said
 * 1x1. Since `grillePattern` defaults to `colonial` on every surface, a window
 * with no stored `panes` — which is EVERY window on a scan whose style was
 * never set — drew a 2x2 divided light in 3D and a single undivided one on the
 * elevation. Reported as "the lite count is wrong in elevations, 3D is right".
 *
 * 2x2 wins for the same reason colonial did: it is what the app already says.
 * The panel opens on 2x2 and 3D draws it; the elevation was the one surface
 * disagreeing with the other three.
 */
export const DEFAULT_PANES = { x: 2, y: 2 };

/**
 * The most lites a pane grid may have across or down — far past any real
 * window, but a cap: nothing limited it, and a grid in the hundreds draws
 * thousands of bars (the user, 2026-10-29). The panel's fields and the
 * `set_window_option` action both stop here.
 */
export const MAX_PANES = 99;

/** A typed pane count as the panel keeps it: a whole number, 1 to MAX_PANES. */
export const paneCountFrom = (typed) =>
  Math.min(MAX_PANES, Math.max(1, parseInt(typed, 10) || 1));

/** No stored value means the whole window is grilled, not just the top sash. */
export const DEFAULT_UPPER_GRILLE_ONLY = false;

/** The frame colours a window may be given. PERSISTED hex values, not UI
 * chrome — never tokenise these. */
export const FRAME_COLORS = [
  { name: 'Aluminum', hex: '#c0c0c0' },
  { name: 'Oil Rubbed Bronze', hex: '#3b2a1a' },
  { name: 'Matte Black', hex: '#1a1a1a' },
  { name: 'Painted White', hex: '#f5f5f5' },
  { name: 'Painted Cream', hex: '#fffdd0' },
  { name: 'Light Wood', hex: '#c4a46c' },
  { name: 'Medium Wood', hex: '#8b6914' },
  { name: 'Dark Wood', hex: '#4a3000' },
];

/**
 * The frame colour a window falls back to — Aluminum, the first swatch in the
 * panel's own `FRAME_COLORS`.
 *
 * Third instance of this file's failure, and the only one that was ALSO
 * visibly wrong. There were three literals for one concept: the panel's
 * `DEFAULT_WINDOW_SETTINGS` said `#c0c0c0`, its seeding effect said
 * `#4a4a4a`, and 3D said `0xc0c0c0`. The `#c0c0c0` copy was dead — its branch
 * only fires when `category.window` is falsy, which never happens for a real
 * window — so the live seed was a dark charcoal against a scene drawn silver.
 *
 * `#4a4a4a` is not in `FRAME_COLORS` at all, so the swatch row showed NOTHING
 * selected and the user could not pick the value that was about to be
 * written; and because `frameColor` is on the persist allow-list, an unrelated
 * edit (picking a grille) stamped the charcoal on for real and the frame
 * darkened in 3D.
 */
export const DEFAULT_FRAME_COLOR = '#c0c0c0';

/**
 * The same colour as the number three.js wants. DERIVED from the string above
 * rather than written out again, because `parseColor` returns its `fallback`
 * verbatim — hand it the '#rrggbb' form and a material silently receives a
 * string where every other path gives it a number. One literal, one
 * conversion, so the two cannot drift.
 */
export const DEFAULT_FRAME_COLOR_HEX = parseInt(
  DEFAULT_FRAME_COLOR.slice(1),
  16
);

/**
 * What a window LOOKS like when nothing has been chosen for it — the twin of
 * `DOOR_APPEARANCE_DEFAULTS`, and the same contract: a default is DISPLAYED,
 * never SEEDED into editable state. The panel holds `undefined` for a field
 * the window does not carry, resolves through here only to draw the control,
 * and so has nothing invented to write back.
 *
 * `panels` is deliberately absent: `null` there is a REAL stored value
 * meaning "auto, derive from width", not an absence, so it keeps its own
 * handling in the panel.
 */
export const WINDOW_APPEARANCE_DEFAULTS = {
  style: 'double-hung',
  frameColor: DEFAULT_FRAME_COLOR,
  panes: DEFAULT_PANES,
  grillePattern: DEFAULT_GRILLE_PATTERN,
  upperGrilleOnly: DEFAULT_UPPER_GRILLE_ONLY,
  trimStyleSideA: 'simple',
  trimStyleSideB: 'simple',
  trimColorSideA: '#ffffff',
  trimColorSideB: '#ffffff',
  bayAngle: 45,
};

/**
 * How many casement sashes a window of this width has.
 *
 * Casements open in pairs from the centre, so the answer is 1 or an even
 * number, targeting ~30" a panel and preferring fewer wide panels to many
 * thin ones.
 *
 * DERIVED, not stored. `category.window.panels` exists only as a manual
 * override and is almost never set — which is why the elevation schematic,
 * whose mullion branch required `panels >= 2`, never drew one: 3D had been
 * computing the count from width all along (`calculateCasementPanels`) and the
 * panel had a third copy of the same arithmetic for its "2 panels (auto from
 * 60″ width)" note. One function now, so a casement cannot be a pair in 3D and
 * a single sash on the elevation.
 *
 * @param {number} widthMeters
 * @returns {number} 1, or an even count
 */
export const casementPanelCount = (widthMeters) => {
  const widthInches = (widthMeters || 0) / 0.0254;
  if (widthInches <= 30) return 1;
  const rawCount = Math.round(widthInches / 30);
  if (rawCount <= 2) return 2;
  return rawCount % 2 === 0 ? rawCount : rawCount + 1;
};

/**
 * How many LEAVES a sliding window of this width has.
 *
 * Sliding panels run wider than casements (~42" vs ~30") because there is no
 * hinge torque — weight and track friction are the limits. Two up to 84",
 * three to 120", and capped there: wider is patio-door territory.
 *
 * Third of the three derivations, and the last to be shared. While it lived
 * alone in `WindowWithTrim` the elevation schematic had nothing to import and
 * hard-coded two leaves, so a 100" slider drew three in 3D and two on paper.
 *
 * @param {number} widthMeters
 * @returns {number} 2 or 3
 */
export const slidingPanelCount = (widthMeters) => {
  const widthInches = (widthMeters || 0) / 0.0254;
  return widthInches <= 84 ? 2 : 3;
};

/**
 * The casement counts a human may pick, in order — the legal range behind the
 * panel's stepper.
 *
 * It is NOT `1..n`: `casementPanelCount` only ever returns 1 or an even
 * number, because casements open in pairs from the centre, and a control that
 * let you step onto 3 would offer a unit the derivation itself cannot produce
 * and the 3D renderer draws with an odd sash at one end. Six is where the
 * derivation tops out for any window that fits a residential wall (~15 ft).
 */
export const CASEMENT_PANEL_CHOICES = [1, 2, 4, 6];

/**
 * How many MULLED units a hung window of this width is made of.
 *
 * Industry sizing: one unit to 42", a pair to 72", three to 108", then ~30"
 * apiece. Same contract as `casementPanelCount` — derived, with
 * `category.window.panels` as the manual override — and shared for the same
 * reason: the 3D renderer and the elevation must divide a window identically.
 *
 * @param {number} widthMeters
 * @returns {number} 1 or more
 */
export const hungUnitCount = (widthMeters) => {
  const widthInches = (widthMeters || 0) / 0.0254;
  if (widthInches <= 42) return 1;
  if (widthInches <= 72) return 2;
  if (widthInches <= 108) return 3;
  return Math.max(4, Math.ceil(widthInches / 30));
};

/**
 * The hung unit counts a human may pick. `hungUnitCount` is uniform above
 * three (~30" a unit), so unlike the casement set this really is a range —
 * six covers every width that fits a residential wall.
 */
export const HUNG_UNIT_CHOICES = [1, 2, 3, 4, 5, 6];

/**
 * The slider leaf counts a human may pick.
 *
 * Two and three, and NOT because the derivation caps there. `createSlidingPanels`
 * decides which leaves operate and where the handle goes with
 * `panelCount === 2 ? … : …`, which is a two-case split: at four leaves it
 * marks three of them operable and draws one handle. The renderer is the
 * limit, so the picker must not offer past it.
 */
export const SLIDING_PANEL_CHOICES = [2, 3];

/**
 * How a window of this style divides, and what a human may override it to —
 * the ONE answer behind the panel's stepper, so a style cannot be offered a
 * count its renderer will not draw.
 *
 * `noun` is the trade word for the division, which differs by style: a
 * casement has panels, a slider has leaves that are also called panels, and a
 * hung window is built from mulled UNITS, each of which then splits into two
 * sashes. Calling all three "panels" in the UI would make the hung count read
 * as a sash count and halve it in the user's head.
 *
 * Returns `null` for a style with no division to choose — picture (one light)
 * and bay (three units fixed by `bayWindowProfile`).
 *
 * @param {string} style
 * @param {number} widthMeters
 * @returns {{auto: number, choices: number[], noun: string} | null}
 */
export const windowDivision = (style, widthMeters) => {
  if (style === 'casement') {
    return {
      auto: casementPanelCount(widthMeters),
      choices: CASEMENT_PANEL_CHOICES,
      noun: 'panel',
    };
  }
  if (style === 'double-hung' || style === 'single-hung') {
    return {
      auto: hungUnitCount(widthMeters),
      choices: HUNG_UNIT_CHOICES,
      noun: 'unit',
    };
  }
  if (style === 'sliding') {
    return {
      auto: slidingPanelCount(widthMeters),
      choices: SLIDING_PANEL_CHOICES,
      noun: 'panel',
    };
  }
  return null;
};
